/**
 * Common V2 Mappings
 *
 * Mappings that apply to all StepKinds that have ActionBar and HeaderBar.
 * Targets: content.actionBar.primary/secondary, content.header.skip/back
 *
 * @module themes/mappers/mappingTablesV2
 */

import type { ThemeV2Mapping } from '../ThemeV2Mapper';
import type { StepKind } from '@segmently/schema-models/v2';
import { addAlpha50 } from './copyBlockMappings';

// ============================================================================
// StepKind Helpers
// ============================================================================

/** StepKinds that have actionBar (all except WebEmbed and Paywall) */
const hasActionBar = (stepKind: StepKind): boolean =>
  stepKind !== 'WebEmbed' && stepKind !== 'Paywall';

/** StepKinds that have header (all except WebEmbed and Paywall) */
const hasHeader = (stepKind: StepKind): boolean =>
  stepKind !== 'WebEmbed' && stepKind !== 'Paywall';

// ============================================================================
// ActionBar Mappings
// ============================================================================

/**
 * Common mappings for ActionBar elements.
 *
 * V2 paths:
 * - Primary button background: actionBar.primary.appearance.backgroundColor
 * - Primary button text color: actionBar.primary.content.appearance.color
 * - Primary button font: actionBar.primary.content.appearance.fontFamily
 * - Secondary button text color: actionBar.secondary.content.appearance.color
 * - Secondary button font: actionBar.secondary.content.appearance.fontFamily
 */
export const ACTION_BAR_MAPPINGS: ThemeV2Mapping[] = [
  // Primary button background = buttonsBackground
  {
    source: 'colors.buttonsBackground',
    target: 'actionBar.primary.appearance.backgroundColor',
    condition: hasActionBar,
  },
  // Primary button text color = buttonsTitle
  {
    source: 'colors.buttonsTitle',
    target: 'actionBar.primary.content.appearance.color',
    condition: hasActionBar,
  },
  // Primary button font = main.fontFamily
  {
    source: 'fonts.main.fontFamily',
    target: 'actionBar.primary.content.appearance.fontFamily',
    condition: hasActionBar,
  },
  // Secondary button text color = buttonsBackground (Primary color)
  {
    source: 'colors.buttonsBackground',
    target: 'actionBar.secondary.content.appearance.color',
    condition: hasActionBar,
  },
  // Secondary button font = main.fontFamily
  {
    source: 'fonts.main.fontFamily',
    target: 'actionBar.secondary.content.appearance.fontFamily',
    condition: hasActionBar,
  },

  // === Layout settings: renderer-backed fields ===
  {
    source: 'layoutSettings.actionBar.insets',
    target: 'actionBar.appearance.insets',
    condition: hasActionBar,
  },
  {
    source: 'layoutSettings.actionBar.primaryButton.radius',
    target: 'actionBar.primary.appearance.borders.radius.uniform',
    condition: hasActionBar,
  },
  {
    source: 'layoutSettings.actionBar.primaryButton.height',
    target: 'actionBar.primary.appearance.dimensions.height',
    condition: hasActionBar,
  },
  {
    source: 'layoutSettings.actionBar.primaryButton.insets',
    target: 'actionBar.primary.appearance.insets',
    condition: hasActionBar,
  },
  {
    source: 'layoutSettings.actionBar.secondaryButton.radius',
    target: 'actionBar.secondary.appearance.borders.radius.uniform',
    condition: hasActionBar,
  },
  {
    source: 'layoutSettings.actionBar.secondaryButton.height',
    target: 'actionBar.secondary.appearance.dimensions.height',
    condition: hasActionBar,
  },
  {
    source: 'layoutSettings.actionBar.secondaryButton.insets',
    target: 'actionBar.secondary.appearance.insets',
    condition: hasActionBar,
  },
];

// ============================================================================
// HeaderBar Mappings
// ============================================================================

/**
 * Common mappings for HeaderBar elements.
 *
 * V2 paths:
 * - Skip button text color: header.skip.content.appearance.color
 * - Skip button font: header.skip.content.appearance.fontFamily
 * - Back button color: header.back.content.appearance.color (icon tint)
 * - Close button color: header.close.content.appearance.color
 */
export const HEADER_BAR_MAPPINGS: ThemeV2Mapping[] = [
  // Skip button text color = titlesSubtitles
  {
    source: 'colors.titlesSubtitles',
    target: 'header.skip.content.appearance.color',
    condition: hasHeader,
  },
  // Skip button font = main.fontFamily
  {
    source: 'fonts.main.fontFamily',
    target: 'header.skip.content.appearance.fontFamily',
    condition: hasHeader,
  },
  // Back button color = titlesSubtitles
  {
    source: 'colors.titlesSubtitles',
    target: 'header.back.content.appearance.color',
    condition: hasHeader,
  },
  // Close button color = titlesSubtitles
  {
    source: 'colors.titlesSubtitles',
    target: 'header.close.content.appearance.color',
    condition: hasHeader,
  },
  // Progress indicator colors follow the same palette cascade as
  // applyToBasicScreen. Keeping them declarative also makes delta propagation
  // leaf-specific instead of replacing the whole header region.
  {
    source: 'colors.buttonsBackground',
    target: 'header.progressIndicator.activeColor',
    condition: hasHeader,
  },
  {
    source: 'colors.titlesSubtitles',
    target: 'header.progressIndicator.trackColor',
    transform: addAlpha50,
    condition: hasHeader,
  },
];

// ============================================================================
// Combined Common Mappings
// ============================================================================

export const COMMON_V2_MAPPINGS: ThemeV2Mapping[] = [
  {
    source: 'webViewBackgroundColor',
    target: 'canvas.webViewBackgroundColor',
    // Theme-level null means disabled. Screen-level null remains a durable
    // per-screen override and is preserved by the delta mapper.
    transform: (value) => typeof value === 'string' && value.length > 0
      ? value
      : undefined,
  },
  ...ACTION_BAR_MAPPINGS,
  ...HEADER_BAR_MAPPINGS,
];
